import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Upload, FileDown, Loader2, AlertTriangle,
  ShieldAlert, PlayCircle, Building2, UserMinus, UserCheck, UserPlus, RefreshCcw, RotateCcw,
} from 'lucide-react';
import { presenzeApi, companiesApi } from '../lib/api';
import { toast } from 'sonner';

const FIELD_LABELS = {
  fiscalCode: 'Codice Fiscale',
  birthDate: 'Data di nascita',
  birthPlace: 'Luogo di nascita',
  mansione: 'Mansione',
  sede: 'Sede/Reparto',
  dataAssunzione: 'Data Assunzione',
  ruoli: 'Ruolo',
};

const renderValue = (v) => {
  if (v == null) return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  const s = String(v).trim();
  return s || '—';
};

const PresenzeImportPage = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [selectedCids, setSelectedCids] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [applyState, setApplyState] = useState(null);
  const [reason, setReason] = useState("Non presente nell'ultimo aggiornamento presenze");
  const [reassign, setReassign] = useState(true);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    companiesApi.list().then((r) => setCompanies(r.data || [])).catch(() => {});
  }, []);

  const filteredCompanies = useMemo(() => {
    const s = companySearch.trim().toLowerCase();
    if (!s) return companies.slice(0, 200);
    return companies.filter((c) => (c.name || '').toLowerCase().includes(s)).slice(0, 200);
  }, [companies, companySearch]);

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === companyId) || null,
    [companies, companyId],
  );

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setPreview(null);
    setSelectedCids(new Set());
    setApplyState(null);
  };

  const doPreview = async () => {
    if (!file) { toast.error('Seleziona un file Excel'); return; }
    setUploading(true); setPreview(null); setApplyState(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (companyId) fd.append('companyId', companyId);
      const { data } = await presenzeApi.preview(fd);
      setPreview(data);
      // Auto-select companies with meaningful actions
      const initial = new Set();
      (data.companies || []).forEach((cr) => {
        if (cr.matched_with_changes > 0 || cr.to_dismiss > 0 || cr.to_create > 0 || cr.to_reactivate > 0) initial.add(cr.companyId);
      });
      setSelectedCids(initial);
      if (data.auto_merge?.pairs_applied) toast.info(`Unite automaticamente ${data.auto_merge.pairs_applied} schede doppie della stessa persona`);
      const t = data.totals;
      toast.success(`Preview: ${t.companies} aziende · ${t.matched_with_changes} aggiornamenti · ${t.to_create || 0} da creare · ${t.to_reactivate || 0} da riattivare · ${t.to_dismiss} da dismettere`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore preview');
    } finally {
      setUploading(false);
    }
  };

  const toggleCid = (cid) => {
    setSelectedCids((prev) => {
      const nx = new Set(prev);
      if (nx.has(cid)) nx.delete(cid); else nx.add(cid);
      return nx;
    });
  };

  const toggleExpand = (cid) => {
    setExpanded((prev) => {
      const nx = new Set(prev);
      if (nx.has(cid)) nx.delete(cid); else nx.add(cid);
      return nx;
    });
  };

  const doApply = async () => {
    if (!preview) return;
    if (selectedCids.size === 0) { toast.error('Nessuna azienda selezionata'); return; }
    const summary = (preview.companies || [])
      .filter((cr) => selectedCids.has(cr.companyId))
      .reduce((acc, cr) => {
        const rea = reassign
          ? (cr.detail.to_create || []).filter((u) => {
              const exact = (u.elsewhere || []).filter((e) => e.match_type === 'esatto' || e.match_type === 'cf');
              const cids = new Set(exact.map((e) => e.companyId));
              return exact.length >= 1 && cids.size === 1;
            }).length
          : 0;
        return {
          upd: acc.upd + cr.matched_with_changes,
          crea: acc.crea + (cr.to_create || 0) - rea,
          ria: acc.ria + (cr.to_reactivate || 0),
          dis: acc.dis + cr.to_dismiss,
          rea: acc.rea + rea,
        };
      }, { upd: 0, crea: 0, ria: 0, dis: 0, rea: 0 });
    if (!window.confirm(
      `Applicare l'aggiornamento presenze?\n\n· Aggiornamenti campi: ${summary.upd}\n· Nuovi lavoratori da creare: ${summary.crea}\n· Dismessi da riattivare: ${summary.ria}\n· Riassegnazioni da altre aziende: ${summary.rea}\n· Lavoratori da DISMETTERE: ${summary.dis}\n\nMotivo dismissione: "${reason}"\n\nBackup automatico via $out (reversibile).`,
    )) return;

    setApplying(true);
    setApplyState({ state: 'starting', phase: 'starting', progress: 0, total: summary.upd + summary.dis + summary.rea + summary.crea + summary.ria });
    try {
      const { data } = await presenzeApi.apply({
        token: preview.token,
        company_ids: Array.from(selectedCids),
        reason,
        reassign,
      });
      const opId = data.opId;
      const poll = setInterval(async () => {
        try {
          const { data: st } = await presenzeApi.status(opId);
          setApplyState(st);
          if (st.state === 'done') {
            clearInterval(poll);
            if (st.auto_merge?.pairs_applied) toast.info(`Unite automaticamente ${st.auto_merge.pairs_applied} schede doppie`);
            toast.success(`Fatto: ${st.total_updated || 0} aggiornati · ${st.total_created || 0} creati · ${st.total_reactivated || 0} riattivati · ${st.total_reassigned || 0} riassegnati · ${st.total_dismissed || 0} dismessi`);
            setApplying(false);
          } else if (st.state === 'error') {
            clearInterval(poll);
            toast.error(`Errore: ${st.error}`);
            setApplying(false);
          }
        } catch (err) {
          clearInterval(poll);
          setApplying(false);
        }
      }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore apply');
      setApplying(false);
    }
  };

  return (
    <div className="space-y-6 pb-16" data-testid="presenze-import-page">
      <div className="flex items-center gap-3 text-sm">
        <Link to="/riconciliazione" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-700">
          <ArrowLeft size={14} /> Torna a Riconciliazione
        </Link>
      </div>

      <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-6">
        <h2 className="text-lg font-bold text-[#023F80] mb-2">Aggiorna Presenze da Excel</h2>
        <p className="text-sm text-slate-600 leading-relaxed mb-4">
          Carica un Excel con <b>Azienda</b>, <b>Cognome/Nome</b> (o unificato) e opzionali <b>Codice Fiscale</b>, <b>Data/Luogo di nascita</b>, <b>Mansione</b>, <b>Sede/Reparto</b>, <b>Ruolo</b>, <b>Data Assunzione</b>.
          Il sistema <b>aggiorna</b> i campi quando differiscono (Excel-wins, celle vuote non toccano il DB), <b>crea</b> i nuovi lavoratori, <b>riattiva</b> i dismessi presenti nel file e <b>dismette in modo reversibile</b> i lavoratori attivi non presenti nel file.
          Backup automatico prima della scrittura.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Azienda predefinita (opzionale)</label>
            <input
              type="text"
              placeholder="Cerca azienda… (opzionale, sostituisce colonna Azienda)"
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 mb-2"
              data-testid="presenze-company-search"
            />
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-md px-3 py-2"
              data-testid="presenze-company-select"
            >
              <option value="">— multi-azienda (usa colonna Azienda) —</option>
              {filteredCompanies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {selectedCompany && (
              <button
                className="text-[11px] text-slate-500 mt-1 underline"
                onClick={() => { setCompanyId(''); setCompanySearch(''); }}
                data-testid="presenze-company-clear"
              >Rimuovi scope azienda</button>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">File Excel</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={onFileChange}
              className="w-full text-sm"
              data-testid="presenze-file-input"
            />
            <button
              onClick={() => presenzeApi.downloadTemplate()}
              className="text-[11px] text-[#023F80] mt-2 inline-flex items-center gap-1 hover:underline"
              data-testid="presenze-template-download"
            >
              <FileDown size={12} /> Scarica template
            </button>
          </div>
          <div className="flex items-end">
            <button
              onClick={doPreview}
              disabled={!file || uploading}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#023F80] text-white text-sm font-semibold rounded-lg hover:bg-[#012a5c] disabled:opacity-50"
              data-testid="presenze-preview-btn"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? 'Analizzo…' : 'Anteprima'}
            </button>
          </div>
        </div>
      </div>

      {preview && (
        <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-slate-800">Anteprima · <span className="text-slate-500 font-normal">{preview.filename}</span></h3>
              <p className="text-xs text-slate-500">
                {preview.totals.companies} aziende · {preview.totals.rows} righe file ·{' '}
                {preview.totals.matched_with_changes} aggiornamenti ·{' '}
                {preview.totals.to_create || 0} da creare ·{' '}
                {preview.totals.to_reactivate || 0} da riattivare ·{' '}
                {preview.totals.to_dismiss} da dismettere ·{' '}
                {preview.totals.unmatched} non matched
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-700 select-none cursor-pointer border border-slate-200 rounded-md px-2 py-1.5 bg-slate-50">
                <input
                  type="checkbox"
                  checked={reassign}
                  onChange={(e) => setReassign(e.target.checked)}
                  data-testid="presenze-reassign-toggle"
                  className="w-3.5 h-3.5"
                />
                Riassegna lavoratori da altre aziende (match esatto)
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="text-xs border border-gray-200 rounded-md px-2 py-1.5 w-72"
                placeholder="Motivo dismissione"
                data-testid="presenze-reason-input"
              />
              <button
                onClick={doApply}
                disabled={applying || selectedCids.size === 0}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                data-testid="presenze-apply-btn"
              >
                {applying ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
                {applying ? 'Applico…' : `Applica (${selectedCids.size})`}
              </button>
            </div>
          </div>

          {preview.orphan_rows_count > 0 && (
            <div className="mb-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-xs">
              <AlertTriangle size={12} className="inline mr-1" />
              {preview.orphan_rows_count} righe senza azienda risolta (non applicabili).
            </div>
          )}

          {applyState && (
            <div className="mb-4 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs" data-testid="presenze-apply-state">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-700">Stato:</span> {applyState.state} · {applyState.phase}
                {applyState.backup_collection && <span className="ml-2 text-slate-500">backup: {applyState.backup_collection}</span>}
              </div>
              {applyState.total > 0 && (
                <div className="mt-1 w-full h-1 bg-slate-200 rounded overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${Math.round((applyState.progress || 0) / applyState.total * 100)}%` }} />
                </div>
              )}
              {applyState.state === 'done' && (
                <div className="mt-1 text-slate-600">✅ {applyState.total_updated || 0} aggiornati · {applyState.total_created || 0} creati · {applyState.total_reactivated || 0} riattivati · {applyState.total_reassigned || 0} riassegnati · {applyState.total_dismissed || 0} dismessi</div>
              )}
              {applyState.state === 'error' && (
                <div className="mt-1 text-red-700">❌ {applyState.error}</div>
              )}
            </div>
          )}

          <div className="space-y-3">
            {preview.companies.map((cr) => {
              const isSel = selectedCids.has(cr.companyId);
              const isExp = expanded.has(cr.companyId);
              return (
                <div key={cr.companyId} className={`border rounded-xl ${isSel ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}>
                  <div className="flex items-center gap-3 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleCid(cr.companyId)}
                      data-testid={`presenze-company-toggle-${cr.companyId}`}
                      className="w-4 h-4"
                    />
                    <Building2 size={16} className="text-[#023F80]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{cr.companyName}</div>
                      <div className="text-[11px] text-slate-500">
                        {cr.rows_total} righe file · {cr.active_workers_total} attivi in DB
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] font-semibold flex-wrap">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800"><RefreshCcw size={10} />{cr.matched_with_changes} da aggiornare</span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700"><UserCheck size={10} />{cr.matched_without_changes} invariati</span>
                      {(cr.to_create || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800"><UserPlus size={10} />{cr.to_create} da creare</span>
                      )}
                      {(cr.to_reactivate || 0) > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-100 text-teal-800"><RotateCcw size={10} />{cr.to_reactivate} da riattivare</span>
                      )}
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800"><UserMinus size={10} />{cr.to_dismiss} da dismettere</span>
                      {cr.unmatched > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"><ShieldAlert size={10} />{cr.unmatched} non matched</span>
                      )}
                    </div>
                    <button
                      onClick={() => toggleExpand(cr.companyId)}
                      className="text-xs text-[#023F80] hover:underline"
                      data-testid={`presenze-company-expand-${cr.companyId}`}
                    >
                      {isExp ? 'Nascondi' : 'Dettagli'}
                    </button>
                  </div>
                  {isExp && (
                    <div className="border-t border-slate-100 px-4 py-3 space-y-3">
                      {cr.detail.matches.filter((m) => Object.keys(m.changes).length > 0).length > 0 && (
                        <div>
                          <div className="text-[11px] font-bold text-blue-800 uppercase tracking-wider mb-1">Aggiornamenti campi (Excel-wins)</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
                            {cr.detail.matches.filter((m) => Object.keys(m.changes).length > 0).slice(0, 200).map((m) => (
                              <div key={m.row_number} className="bg-white border border-blue-100 rounded px-2 py-1">
                                <span className="font-semibold">{m.worker.lastName} {m.worker.firstName}</span>
                                {Object.entries(m.changes).map(([field, ch]) => (
                                  <div key={field} className="text-[11px] text-slate-500">
                                    {FIELD_LABELS[field] || field}: <span className="line-through text-slate-400">{renderValue(ch.before)}</span> → <span className="text-slate-800">{renderValue(ch.after)}</span>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {(cr.detail.to_create || []).length > 0 && (
                        <div>
                          <div className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider mb-1">Nuovi lavoratori da creare ({cr.detail.to_create.length})</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
                            {cr.detail.to_create.slice(0, 300).map((nc) => (
                              <div key={nc.row_number} className="bg-white border border-emerald-100 rounded px-2 py-1">
                                <div className="font-semibold">
                                  {nc.input.lastName || nc.input.fullName} {nc.input.firstName}
                                </div>
                                <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-2">
                                  {nc.input.fiscalCode && <span>CF: {nc.input.fiscalCode}</span>}
                                  {nc.input.mansione && <span>· {nc.input.mansione}</span>}
                                  {nc.input.sede && <span>· {nc.input.sede}</span>}
                                  {nc.input.dataAssunzione && <span>· Assunz: {nc.input.dataAssunzione}</span>}
                                  {(nc.input.ruoli || []).length > 0 && <span>· Ruolo: {nc.input.ruoli.join(', ')}</span>}
                                </div>
                                {nc.elsewhere && nc.elsewhere.length > 0 && (
                                  <div className="mt-0.5 text-[10px] text-indigo-700">
                                    ⚠ Esiste già in: {nc.elsewhere.map((e) => `${e.lastName} ${e.firstName} → ${e.companyName} (${e.match_type})`).join(' · ')}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {(cr.detail.to_reactivate || []).length > 0 && (
                        <div>
                          <div className="text-[11px] font-bold text-teal-800 uppercase tracking-wider mb-1">Dismessi da riattivare ({cr.detail.to_reactivate.length})</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
                            {cr.detail.to_reactivate.slice(0, 200).map((ra) => (
                              <div key={ra.row_number} className="bg-white border border-teal-100 rounded px-2 py-1">
                                <div className="font-semibold">{ra.worker.lastName} {ra.worker.firstName}</div>
                                <div className="text-[10px] text-slate-500">
                                  dismesso il {ra.worker.dismessoAt || '—'} ({ra.worker.dismissalReason || 'senza motivo'})
                                  {ra.match_reason && <> · {ra.match_reason}</>}
                                </div>
                                {Object.keys(ra.changes || {}).length > 0 && (
                                  <div className="mt-0.5 text-[10px] text-slate-500">
                                    Aggiornerà: {Object.keys(ra.changes).map((f) => FIELD_LABELS[f] || f).join(', ')}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {cr.detail.to_dismiss.length > 0 && (
                        <div>
                          <div className="text-[11px] font-bold text-red-800 uppercase tracking-wider mb-1">Da dismettere ({cr.detail.to_dismiss.length})</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 text-xs">
                            {cr.detail.to_dismiss.slice(0, 500).map((w) => (
                              <div key={w.id} className="bg-white border border-red-100 rounded px-2 py-1">
                                <span className="font-semibold">{w.lastName} {w.firstName}</span>
                                {w.mansione && <span className="text-[10px] text-slate-500 ml-1">· {w.mansione}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {cr.detail.unmatched.length > 0 && (
                        <div>
                          <div className="text-[11px] font-bold text-amber-800 uppercase tracking-wider mb-1">Righe non matched ({cr.detail.unmatched.length})</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
                            {cr.detail.unmatched.slice(0, 200).map((u) => (
                              <div key={u.row_number} className="bg-white border border-amber-100 rounded px-2 py-1">
                                <div>
                                  <span className="font-semibold">Riga {u.row_number}:</span>{' '}
                                  {u.input.lastName || u.input.fullName} {u.input.firstName}
                                  <span className="ml-2 text-[10px] text-slate-500">— {u.reason}</span>
                                </div>
                                {u.candidates && u.candidates.length > 0 && (
                                  <div className="mt-0.5 text-[10px] text-slate-500">
                                    Candidati: {u.candidates.map((c) => `${c.lastName} ${c.firstName}`).join(' · ')}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PresenzeImportPage;
